import React from 'react';

import { CardListPanel } from '@eika-kortbetjening/card-list-panel';
import { ErrorMessage } from '@eika/attention-message';
import { SmallButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { ListPanel, ListPanelWrapper } from '@eika/link-panel';

import { useCreditAccountsList } from '@/domain/account';
import { SearchParams, useLinkProducer } from '@/domain/app';
import { useCreditCardsList, CreditCardService } from '@/domain/card';
import { BankRegNr } from '@/domain/session';
import useText from '@/hooks/use-text';
import piwik from '@/piwik/piwik';

import { useSession } from '@components/app/Session';
import CardSkeleton from '@components/skeleton/CardSkeleton';
import { CreditCard as CreditCardI } from '@generated-types';
import { useKbOrderNewCreditCardUrl } from '@hooks/url/use-order-new-credit-card-url';
import { isOver18 } from '@utils/date-utils';

const CANT_SELL_CREDIT_CARD = [BankRegNr.NORD_DIREKTE, BankRegNr.PENNI];

const CreditCardsContent = () => {
    const cardsQuery = useCreditCardsList({ enabled: true });
    const accountsQuery = useCreditAccountsList();
    const produceLink = useLinkProducer();
    const orderNewCreditCardUrl = useKbOrderNewCreditCardUrl();
    const session = useSession();

    const cantSellCreditCard =
        CANT_SELL_CREDIT_CARD.includes(session?.bank?.registrationNumber as BankRegNr) || !orderNewCreditCardUrl;

    const text = useText();

    if (cardsQuery.isError || accountsQuery.isError) {
        const refetch = () => {
            if (cardsQuery.isError) {
                cardsQuery.refetch();
            }
            if (accountsQuery.isError) {
                accountsQuery.refetch();
            }
        };
        return (
            <ErrorMessage
                text={text.messageFetchCardsForCardsOverviewError}
                icon={IconTypes.LUKK_24}
                clickableItem={<SmallButton onClick={refetch}>{text.genericTryAgain}</SmallButton>}
            />
        );
    }

    if ((cardsQuery.isPending && cardsQuery.isFetching) || (accountsQuery.isPending && accountsQuery.isFetching)) {
        return <CardSkeleton />;
    }

    const matchedCards =
        !cardsQuery.isPending && !accountsQuery.isPending
            ? CreditCardService.matchWithAccount(cardsQuery.data, accountsQuery.data).filter(
                  ([card]) => card.userIsCardOwner,
              )
            : [];

    const cards = matchedCards.length
        ? matchedCards.map(([card, account]) =>
              CreditCardService.toKbjPanel(card, (card: CreditCardI) =>
                  produceLink('CARD_PAGE')({
                      [SearchParams.CARD_ID]: card.id,
                      [SearchParams.CARD_TYPE]: 'CREDIT',
                      [SearchParams.ACCOUNT_ID]: account.accountId,
                  }),
              ),
          )
        : [];

    if (cards.length === 0 && cantSellCreditCard) {
        return null;
    }

    return (
        <ListPanelWrapper
            data-track-id={piwik?.cardOverview?.cardListMyCreditCards?.dataTrackId}
            data-track-linkname={piwik?.cardOverview?.cardListMyCreditCards?.dataTrackLinkname}
            id={piwik?.cardOverview?.cardListMyCreditCards?.dataTrackId}
            titleContent={<h3 className="h3 card-list-panel-title">{text.creditCardsTitle}</h3>}
            className="card-list-wrapper"
            contentWrapperAs="ul"
        >
            {cards.map(([card, meta]) => (
                <CardListPanel key={card.number} dictionary={text} card={card} cardMeta={meta} />
            ))}
            {cards.length <= 1 && !cantSellCreditCard && (
                <ListPanel
                    url={orderNewCreditCardUrl}
                    title={text.orderCardCreditCardOption}
                    backgroundColor="light"
                    icon={IconTypes.PLUSS_24}
                    invertIcon
                />
            )}
        </ListPanelWrapper>
    );
};

export const CreditCards = () => {
    const session = useSession();

    if (session.user && isOver18(session.user.birthDate!)) {
        return <CreditCardsContent />;
    }

    return null;
};
